import React, { FormEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { Button, LoaderDots } from 'components';
import { STATE_ABVS } from 'config/const';
import { lineHeight, theme, toRem } from 'design';
import { ImportScript } from 'hooks';
import { selectAPIRequestErrors, selectIsLoadingAPIRequest } from 'store/ApiClient';
import { selectCardSession } from 'store/App';
import { Card, createTokenizedCardRequest } from 'store/Card';
import { IStore } from 'store/types';
import { selectCurrentUser } from 'store/User';
import { CardType, IApiErrorPayloadWithCode, ITokenizeCardRequest } from 'types';
import { isProduction } from 'utils';

interface IProps {
  className?: string;
  onScriptLoaded?: () => void;
  onError?: (error: IApiErrorPayloadWithCode) => void;
}

interface IVGSCardField {
  errorMessages: string[];
  isDirty: boolean;
  isEmpty: boolean;
  isFocused: boolean;
  isTouched: boolean;
  isValid: boolean;
  name: string;
}

interface IVGSCardNumberField extends IVGSCardField {
  bin: string;
  cardType: string;
  last4: string;
}

interface IVGSCardState {
  address_1: IVGSCardField;
  address_2: IVGSCardField;
  city: IVGSCardField;
  cvv: IVGSCardField;
  exp_date: IVGSCardField;
  first_name: IVGSCardField;
  last_name: IVGSCardField;
  card_number: IVGSCardNumberField;
  state: IVGSCardField;
  zip_code: IVGSCardField;
}

interface IVGSCardResponse {
  address_1: string;
  address_2: string;
  city: string;
  cvv: string;
  exp_date: string;
  first_name: string;
  last_name: string;
  card_number: string;
  state: string;
  zip_code: string;
}

const SCRIPT_ID = 'vgs-collect-script';

/**
 * VGS Collect is loaded from a version-pinned, immutable URL and verified with the SHA-384
 * Subresource Integrity hash VGS publishes for that exact version. If the bytes served differ by
 * so much as one character, the browser refuses to execute the file.
 *
 * This is the card capture script. A compromise of it — at VGS, at their CDN, or anywhere in
 * between — would be able to read the PAN before tokenization. SRI is the control that makes that
 * substitution fail closed instead of succeeding silently.
 *
 * VERSION AND HASH MUST BE CHANGED TOGETHER. They are one constant for that reason: bumping the
 * version and leaving a stale hash breaks card capture entirely in production.
 *
 * To upgrade, take the published hash for the new version from VGS's changelog and verify it
 * against the file yourself before shipping — do not trust the page alone:
 *
 *   curl -fsSL <url> | openssl dgst -sha384 -binary | openssl base64 -A
 *
 * Source: https://docs.verygoodsecurity.com/vault/developer-tools/vgs-collect/js/changelog
 * Verified against the live file 2026-08-07.
 */
const VGS_COLLECT = {
  version: '2.15.0',
  integrity: 'sha384-bFaJ8ofJs+NMcVRQ+iDcxmehVD+sldFjGhWbcYCqI3g4LinZHlzrxd4kSiNACOed',
} as const;

const SCRIPT_URL = `https://js.verygoodvault.com/vgs-collect/${VGS_COLLECT.version}/vgs-collect.js`;
const NAME_VALIDATION_REGEX = "/^(?=(?:.*\\S){2})(?=.*[A-Za-z])(?!^[\\d\\s]+$)(?!^[\\-\\s]+$)(?!^[\\d\\-\\s]+$)[A-Za-z0-9 \\-']*$/";
const LOCATION_VALIDATION_REGEX = "/^(?=.*[A-Za-z])[A-Za-z0-9 .,#'\\-\\/]{2,}$/";
const VGSCollectPaymentMethodFormLayout = styled.form``;

const FormField = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;

  &:nth-child(2) {
    margin-left: ${toRem(theme.space.lg)};
  }

  label {
    margin-bottom: ${toRem(theme.space.sm)};
    font-size: ${toRem(theme.font.size.md)};
    line-height: ${lineHeight(theme.font.size.md)};
    font-weight: ${theme.font.weight.medium};
  }

  .form-field {
    display: block;
    width: 100%;
    height: ${theme.input.height.large}px;
    padding: ${toRem(theme.space.sm)} ${toRem(theme.space.md)};
    font-size: ${toRem(theme.font.size.md)};
    line-height: ${lineHeight(theme.font.size.md)};
    color: ${theme.color.neutral[600]};
    background-color: ${theme.color.white};
    background-clip: padding-box;
    border: 1px solid ${theme.color.neutral[300]};
    border-radius: ${theme.borderRadius.xsmall}px;
    transition:
      border-color 0.15s ease-in-out,
      box-shadow 0.15s ease-in-out;

    iframe {
      width: 100%;
      height: 100%;
    }
  }
`;

const FormFieldError = styled.p`
  margin-top: ${toRem(theme.space.sm)};
  font-size: ${toRem(theme.font.size.xxs)};
  color: ${theme.color.danger[50]};

  &:first-letter {
    text-transform: uppercase;
  }
`;

const FormFieldRow = styled.div`
  display: flex;
  width: 100%;
  margin-bottom: ${toRem(theme.space.lg)};
`;

const FormFieldsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: ${toRem(theme.space.xl)};
  padding: ${toRem(theme.space.xl)};
  width: 100%;
  border-radius: ${theme.borderRadius.small}px;
  background: ${theme.color.neutral[100]};
`;

const PaymentMethodFormCta = styled(Button)`
  width: 200px;
`;

const FormError = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  margin-bottom: ${toRem(theme.space.xl)};
  padding: ${toRem(theme.space.xl)};
  border-radius: ${theme.borderRadius.small}px;
  background: ${theme.color.danger[25]};
  margin-top: ${toRem(theme.space.sm)};
  font-size: ${toRem(theme.font.size.xxs)};
  color: ${theme.color.danger[50]};

  p {
    font-size: ${toRem(theme.font.size.sm)};
    color: ${theme.color.neutral[900]};
    text-align: center;
    line-height: ${lineHeight(theme.font.size.sm)};

    &:first-child {
      font-weight: ${theme.font.weight.semiBold};
    }

    &:last-child {
      font-weight: ${theme.font.weight.regular};
    }
  }
`;

const RequiredStar = styled.span`
  margin-left: ${toRem(theme.space.xs)};
`;

const initFields: { [key: string]: any } = {
  address_1: null,
  address_2: null,
  city: null,
  cvv: null,
  exp_date: null,
  first_name: null,
  last_name: null,
  card_number: null,
  state: null,
  zip_code: null,
};

const getNetwork = (cardNumber: string) => {
  switch (cardNumber.substring(0, 1)) {
    case '3':
      return CardType.AMERICAN_EXPRESS.toString().toLowerCase();
    case '4':
      return CardType.VISA.toString().toLowerCase();
    case '5':
      return CardType.MASTER_CARD.toString().toLowerCase();
    case '6':
      return CardType.DISCOVER.toString().toLowerCase();
    default:
      return CardType.OTHER.toString().toLowerCase();
  }
};

const states = STATE_ABVS.map((state) => ({ value: state, text: state }));

export const VGSCollectPaymentMethodForm: React.FC<IProps> = ({ className = '', onScriptLoaded, onError }) => {
  const dispatch = useDispatch();

  const user = useSelector(selectCurrentUser());
  const userId = user?.id;
  const cardSession = useSelector(selectCardSession());
  const sessionId = cardSession?.sessionId;
  const apiErrors = useSelector((state: IStore) => selectAPIRequestErrors(state, Card.CREATE_TOKENIZED_CARD_REQUEST)) as IApiErrorPayloadWithCode;

  const addCardLoading = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, Card.CREATE_TOKENIZED_CARD_REQUEST));
  const [form, setForm] = useState<any>(null);
  const [scriptLoaded, setScriptLoaded] = useState(false);
  const [scriptFailed, setScriptFailed] = useState(false);
  const [vgsState, setVGSState] = useState<IVGSCardState>(null);
  const [fields, setFields] = useState<{ [key: string]: any }>(initFields);
  const [isProcessing, setIsProcessing] = useState(false);
  const [submissionError, setSubmissionError] = useState('');
  const disableCta = useMemo(() => {
    if (!form || !scriptLoaded || !vgsState || addCardLoading) return true;

    return Object.values(vgsState).some((field) => field.isValid === false);
  }, [form, scriptLoaded, vgsState, addCardLoading]);

  useEffect(() => {
    const script = document.getElementById(SCRIPT_ID);
    if (script) script.remove();

    ImportScript({
      async: true,
      id: SCRIPT_ID,
      resourceUrl: SCRIPT_URL,
      integrity: VGS_COLLECT.integrity,
      crossOrigin: 'anonymous',
      onload: () => {
        setScriptLoaded(true);
        onScriptLoaded?.();
      },
      // Fires if the script cannot be fetched, or if it is fetched but fails the integrity check.
      // Without this the form renders a spinner indefinitely and tells the user nothing — see the
      // early return below. An integrity failure means the bytes served were not the bytes we
      // pinned, so the right behaviour is to refuse to collect a card, not to degrade quietly.
      onerror: () => setScriptFailed(true),
    });
  }, []);

  useEffect(() => {
    if (!apiErrors?.errorMessages?.length) return;

    switch (apiErrors.statusCode) {
      case '422':
        setSubmissionError(apiErrors.errorMessages[0]);
        break;
      default:
        if (onError) {
          onError(apiErrors);
        } else {
          setSubmissionError('Please try again.');
        }
        break;
    }

    setIsProcessing(false);
  }, [apiErrors?.errorMessages]);

  useEffect(() => {
    Object.values(fields).forEach((field) => field?.update({ readOnly: addCardLoading ? 'readonly' : null }));
  }, [fields, addCardLoading]);

  useEffect(() => {
    if (!scriptLoaded) return;

    const vgsForm = (window as any).VGSCollect.create(process.env.REACT_APP_VGS_VAULT_ID, process.env.REACT_APP_VGS_ENV, (state: IVGSCardState) => setVGSState(state));

    if (!vgsForm) return;

    setForm(vgsForm);

    const updatedFields = { ...fields };

    updatedFields.first_name = vgsForm.field('#cc-first-name', {
      type: 'text',
      name: 'first_name',
      validations: ['required', NAME_VALIDATION_REGEX],
      tokenization: false,
      readOnly: null,
      maxLength: 75,
      autoComplete: 'given-name',
    });

    updatedFields.last_name = vgsForm.field('#cc-last-name', {
      type: 'text',
      name: 'last_name',
      validations: ['required', NAME_VALIDATION_REGEX],
      tokenization: false,
      readOnly: null,
      maxLength: 75,
      autoComplete: 'family-name',
    });

    updatedFields.card_number = vgsForm.field('#cc-number', {
      type: 'card-number',
      name: 'card_number',
      showCardIcon: true,
      validations: ['required', 'validCardNumber'],
      tokenization: { format: 'FPE_SIX_T_FOUR', storage: 'PERSISTENT' },
      readOnly: null,
      autoComplete: 'cc-number',
      ...(!isProduction() && {
        addCardBrands: [
          {
            type: 'other',
            pattern: /^9/,
            format: /(\d{1,4})/g,
            length: [16],
            cvcLength: [3],
            luhn: true,
          },
        ],
      }),
    });

    updatedFields.exp_date = vgsForm.field('#cc-expiration-date', {
      type: 'card-expiration-date',
      name: 'exp_date',
      validations: ['required', 'validCardExpirationDate'],
      tokenization: false,
      readOnly: null,
      yearLength: '2',
      autoComplete: 'cc-exp',
    });

    updatedFields.cvv = vgsForm.field('#cc-cvv', {
      type: 'card-security-code',
      name: 'cvv',
      maxLength: 4,
      validations: ['required', 'validCardSecurityCode'],
      tokenization: { format: 'NUM_LENGTH_PRESERVING', storage: 'VOLATILE' },
      readOnly: null,
      autoComplete: 'cc-csc',
    });

    updatedFields.address_1 = vgsForm.field('#cc-address-1', {
      type: 'text',
      name: 'address_1',
      validations: ['required', LOCATION_VALIDATION_REGEX],
      tokenization: false,
      readOnly: null,
      maxLength: 200,
      autoComplete: 'street-address',
    });

    updatedFields.address_2 = vgsForm.field('#cc-address-2', {
      type: 'text',
      name: 'address_2',
      validations: [],
      tokenization: false,
      readOnly: null,
      maxLength: 50,
      autoComplete: 'address-line2',
    });

    updatedFields.city = vgsForm.field('#cc-city', {
      type: 'text',
      name: 'city',
      validations: ['required', LOCATION_VALIDATION_REGEX],
      tokenization: false,
      readOnly: null,
      maxLength: 100,
      autoComplete: 'address-level2',
    });

    updatedFields.state = vgsForm.field('#cc-state', {
      type: 'dropdown',
      name: 'state',
      validations: ['required'],
      options: states,
      tokenization: false,
      readOnly: null,
      autoComplete: 'address-level1',
    });

    updatedFields.zip_code = vgsForm.field('#cc-zip', {
      type: 'zip-code',
      name: 'zip_code',
      validations: ['required', 'postal_code/us'],
      tokenization: false,
      readOnly: null,
      autoComplete: 'postal-code',
    });

    setFields(updatedFields);
  }, [scriptLoaded]);

  const onVGSError = useCallback(
    (error: IVGSCardState) => {
      setVGSState({
        ...vgsState,
        ...error,
      });

      setIsProcessing(false);
    },
    [vgsState]
  );

  const onVGSSuccess = useCallback(
    (_: any, { cvv, exp_date, first_name, last_name, address_1, address_2, city, state, zip_code, card_number }: IVGSCardResponse) => {
      const cardRequest: ITokenizeCardRequest = {
        cvv,
        expDate: exp_date.split(' ').join(''),
        last4: card_number.substring(card_number.length - 4),
        network: getNetwork(card_number.substring(0, 1)),
        owner: {
          firstName: first_name,
          lastName: last_name,
          address1: address_1,
          address2: address_2,
          city,
          state,
          zipCode: zip_code,
        },
        cardNumber: card_number,
        userId,
        sessionId,
      };

      dispatch(createTokenizedCardRequest(cardRequest));
    },
    [userId, sessionId]
  );

  // build a friendly error message
  const buildErrorMessage = (fieldState: IVGSCardField) => {
    let fieldName = fieldState.name.replaceAll('_', ' ');
    // Set name
    switch (fieldState.name) {
      case 'exp_date': {
        fieldName = 'expiration';
        break;
      }
      case 'address_1': {
        fieldName = 'billing address';
        break;
      }
      case 'cvv': {
        fieldName = 'CVV';
        break;
      }
      default:
        break;
    }

    // Set error
    if (fieldState.errorMessages[0] === 'is required') {
      return `${fieldName} ${fieldState.errorMessages[0]}`;
    }

    if (fieldState.errorMessages[0] === "the value doesn't match a pattern") {
      if (fieldState.name === 'first_name' || fieldState.name === 'last_name') {
        return 'min 2 characters, 1 letter required; letters, numbers, spaces, apostrophes, and hyphens only.';
      }
      return `please enter a valid ${fieldName}.`;
    }

    return fieldState.errorMessages[0];
  };

  const handleSubmit = useCallback(
    (e: FormEvent) => {
      e.preventDefault();
      if (!form) return;

      setIsProcessing(true);

      form.tokenize(onVGSSuccess, onVGSError);
    },
    [form, onVGSSuccess, onVGSError]
  );

  const renderError = (fieldState: IVGSCardField) => {
    if (!fieldState?.isTouched || !fieldState?.errorMessages?.length || fieldState.isFocused) return null;

    return (
      <FormFieldError className={fieldState.name} data-cy={fieldState.name}>
        {buildErrorMessage(fieldState)}
      </FormFieldError>
    );
  };

  const renderSubmissionError = () => {
    if (!submissionError) return null;

    return (
      <FormError className="form-error">
        <p>Something went wrong!</p>
        <p>{submissionError}</p>
      </FormError>
    );
  };

  // Ordered before the loading check on purpose: a failed load never sets scriptLoaded, so without
  // this the failure is indistinguishable from "still loading" and the user waits on a spinner that
  // will never resolve. Fail visibly instead.
  if (scriptFailed) {
    return (
      <FormError className="form-error" data-cy="vgs-script-error">
        <p>We could not load the secure card form.</p>
        <p>Your card was not submitted. Please refresh the page and try again — if it keeps happening, contact support.</p>
      </FormError>
    );
  }

  if (!scriptLoaded) return <LoaderDots />;

  return (
    <VGSCollectPaymentMethodFormLayout className={className} onSubmit={handleSubmit}>
      <FormFieldsWrapper>
        <FormFieldRow>
          <FormField>
            <label htmlFor="cc-first-name">
              First Name<RequiredStar>*</RequiredStar>
            </label>
            <div id="cc-first-name" className="form-field" />
            {renderError(vgsState?.first_name)}
          </FormField>
          <FormField>
            <label htmlFor="cc-last-name">
              Last Name<RequiredStar>*</RequiredStar>
            </label>
            <div id="cc-last-name" className="form-field" />
            {renderError(vgsState?.last_name)}
          </FormField>
        </FormFieldRow>

        <FormFieldRow>
          <FormField>
            <label htmlFor="cc-number">
              Card Number<RequiredStar>*</RequiredStar>
            </label>
            <div id="cc-number" className="form-field" />
            {renderError(vgsState?.card_number)}
          </FormField>
        </FormFieldRow>

        <FormFieldRow>
          <FormField>
            <label htmlFor="cc-expiration-date">
              Expiration<RequiredStar>*</RequiredStar>
            </label>
            <div id="cc-expiration-date" className="form-field" />
            {renderError(vgsState?.exp_date)}
          </FormField>
          <FormField>
            <label htmlFor="cc-cvv">
              CVV<RequiredStar>*</RequiredStar>
            </label>
            <div id="cc-cvv" className="form-field" />
            {renderError(vgsState?.cvv)}
          </FormField>
        </FormFieldRow>

        <FormFieldRow>
          <FormField>
            <label htmlFor="cc-address-1">
              Billing Address<RequiredStar>*</RequiredStar>
            </label>
            <div id="cc-address-1" className="form-field" />
            {renderError(vgsState?.address_1)}
          </FormField>
        </FormFieldRow>

        <FormFieldRow>
          <FormField>
            <label htmlFor="cc-address-2">Unit / Apt #</label>
            <div id="cc-address-2" className="form-field" />
            {renderError(vgsState?.address_2)}
          </FormField>
        </FormFieldRow>

        <FormFieldRow>
          <FormField>
            <label htmlFor="cc-city">
              City<RequiredStar>*</RequiredStar>
            </label>
            <div id="cc-city" className="form-field" />
            {renderError(vgsState?.city)}
          </FormField>
        </FormFieldRow>

        <FormFieldRow>
          <FormField>
            <label htmlFor="cc-state">
              State<RequiredStar>*</RequiredStar>
            </label>
            <div id="cc-state" className="form-field" />
            {renderError(vgsState?.state)}
          </FormField>
          <FormField>
            <label htmlFor="cc-zip">
              Zip<RequiredStar>*</RequiredStar>
            </label>
            <div id="cc-zip" className="form-field" />
            {renderError(vgsState?.zip_code)}
          </FormField>
        </FormFieldRow>
      </FormFieldsWrapper>

      {renderSubmissionError()}

      <PaymentMethodFormCta appearance="primary" label="Pay To This Card" data-cy="add-payment-method-submit-button" disabled={disableCta} isProcessing={addCardLoading || isProcessing} type="submit" />
    </VGSCollectPaymentMethodFormLayout>
  );
};
